import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { LiaChatDrawer } from '@/components/LiaChatDrawer';
import { useDiagnostic, useBadges, useUpcomingEvents } from '@/hooks/useParticipantData';
import { getBadgeMeta } from '@/lib/badgeMeta';
import { useProjects } from '@/hooks/useProjects';
import { useSelectedCohort } from '@/hooks/useSelectedCohort';
import { useWeeklyProgress, getCurrentWeek } from '@/hooks/useWeeklyProgress';
import { NPSInlineWidget } from '@/components/NPSInlineWidget';
import { CertificationHeader } from '@/components/CertificationHeader';
import { ProgressRing } from '@/components/ui/progress-ring';
import { WeeklyProgressAccordion } from '@/components/WeeklyProgressAccordion';
import { StreakRankingWidget } from '@/components/StreakRankingWidget';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertTriangle, Calendar, ClipboardCheck, ArrowRight, Trophy, CheckCircle2, BookOpen, CalendarDays, FileText, Video, MessageCircle, HelpCircle, Briefcase, Beaker, Zap } from 'lucide-react';
import { PendingItemsWidget } from '@/components/PendingItemsWidget';
import CertificationsWidget from '@/components/profile/CertificationsWidget';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { differenceInDays, format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Skeleton } from '@/components/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const MATURITY_LEVELS: Record<number, { label: string; color: string; bg: string }> = {
  1: { label: 'Nivel Iniciador', color: 'text-muted-foreground', bg: 'bg-muted' },
  2: { label: 'Nivel Explorador', color: 'text-accent', bg: 'bg-accent/20' },
  3: { label: 'Nivel Implementador', color: 'text-warning', bg: 'bg-warning/20' },
  4: { label: 'Nivel Integrador', color: 'text-primary', bg: 'bg-primary/20' },
  5: { label: 'Nivel Estratega', color: 'text-success', bg: 'bg-success/20' },
  6: { label: 'Nivel Transformador', color: 'text-success', bg: 'bg-success/20' },
};

export function ParticipantDashboard() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [liaChatOpen, setLiaChatOpen] = useState(false);
  const [pendingModalSection, setPendingModalSection] = useState<'content' | 'deliverables' | null>(null);

  const { data: diagnostic, isLoading: loadingDiag } = useDiagnostic();
  const { data: badges = [] } = useBadges();
  const { enrollments, selectedCohortId, activeEnrollment, setSelectedCohortId } = useSelectedCohort();
  const { data: projects = [] } = useProjects();

  // Si llegamos desde una ruta concreta (?cohort=...), respetar esa selección
  // en vez de caer a la cohorte acompañada por defecto.
  const [searchParams] = useSearchParams();
  const cohortParam = searchParams.get('cohort');
  useEffect(() => {
    if (!cohortParam || cohortParam === selectedCohortId) return;
    if (enrollments.some(e => e.cohort_id === cohortParam)) {
      setSelectedCohortId(cohortParam);
    }
  }, [cohortParam, selectedCohortId, enrollments, setSelectedCohortId]);
  // Ruta de autoaprendizaje (cohorte perpetua): las semanas se calculan desde el
  // inicio del plan del usuario, no desde start_date (que es nulo en perpetuas).
  const isPerpetualRoute = !!activeEnrollment?.is_perpetual;
  const routeStartDate = isPerpetualRoute
    ? (activeEnrollment?.plan_start_at ?? undefined)
    : (activeEnrollment?.week_start_date ?? undefined);
  const { data: weeklyData = [] } = useWeeklyProgress(activeEnrollment?.program_id, routeStartDate, activeEnrollment?.cohort_id);

  // Última semana de la cohorte (para disparar el widget de NPS): comparamos la
  // semana calendario actual contra la última semana con contenido del programa.
  const lastProgramWeek = weeklyData.length > 0 ? Math.max(...weeklyData.map(w => w.weekNumber)) : null;
  const isLastCohortWeek = !isPerpetualRoute && !!activeEnrollment?.cohort_id && lastProgramWeek !== null
    && getCurrentWeek(activeEnrollment.week_start_date ?? undefined) >= lastProgramWeek;

  const cohortId = selectedCohortId;
  const { data: upcomingEvents = [] } = useUpcomingEvents(cohortId ?? undefined);

  // Check if user already has a certificate for this cohort
  const { data: certificateAvailable = false } = useQuery({
    queryKey: ['has-certificate', profile?.id, cohortId],
    enabled: !!profile?.id && !!cohortId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('certificate_logs')
        .select('id')
        .eq('user_id', profile!.id)
        .eq('cohort_id', cohortId!)
        .limit(1)
        .maybeSingle();
      if (error) return false;
      return !!data;
    },
  });

  // Estado de revisión del proyecto en esta cohorte (para mostrar "Rechazado" con motivo)
  const { data: projectReview } = useQuery({
    queryKey: ['project-review', profile?.id, cohortId],
    enabled: !!profile?.id && !!cohortId,
    queryFn: async () => {
      // cast a any: rejected_at/rejection_reason aún no están en types.ts generado.
      // Match cohort_id = cohortId O null (igual que hasSubmittedProjectFallback),
      // para no perder proyectos de equipo/legacy sin cohorte.
      const { data } = await (supabase as any)
        .from('participant_projects')
        .select('rejected_at, rejection_reason, approved_at, cohort_id')
        .eq('user_id', profile!.id)
        .or(`cohort_id.eq.${cohortId},cohort_id.is.null`)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
  });
  const projectRejected = !!(projectReview as any)?.rejected_at;
  const rejectionReason = (projectReview as any)?.rejection_reason ?? null;
  const projectApproved = !!(projectReview as any)?.approved_at;

  const firstName = profile?.full_name?.split(' ')[0] || 'Participante';
  const programName = activeEnrollment?.program_name || 'Certificación Profesional LIDER IA';

  // Derive progress from weekly data (mandatory content + deliverables)
  const { overallProgress, pendingContent, pendingDeliverables, pendingContentList, pendingDeliverableList, projectMilestoneComplete, challengeMilestoneComplete, hasProjectMilestone, hasChallengeMilestone } = useMemo(() => {
    let totalTasks = 0;
    let doneTasks = 0;
    let pendCont = 0;
    let pendDel = 0;
    const contentList: { weekTitle: string; name: string; contentId: string }[] = [];
    const deliverableList: { weekTitle: string; title: string; refId?: string }[] = [];
    let projMilestone = false;
    let chMilestone = false;
    let hasProjMilestone = false;
    let hasChMilestone = false;

    for (const week of weeklyData) {
      for (const task of week.tasks) {
        if (task.type === 'live') continue;
        if (task.type === 'milestone') {
          if (task.milestoneKind === 'project') {
            hasProjMilestone = true;
            if (task.isComplete) projMilestone = true;
          } else if (task.milestoneKind === 'challenge_lab') {
            hasChMilestone = true;
            if (task.isComplete) chMilestone = true;
          }
          // Los hitos cuentan para el progreso global del programa.
          totalTasks += task.total;
          doneTasks += task.completed;
          continue;
        }
        totalTasks += task.total;
        doneTasks += task.completed;
        if (task.type === 'content') {
          pendCont += task.total - task.completed;
          for (const item of task.pendingItems ?? []) {
            contentList.push({ weekTitle: week.title, name: item.name, contentId: item.contentId });
          }
        }
        // No contar como pendiente el entregable cuya sesión en vivo aún no se dictó.
        if (task.type === 'deliverable' && !task.isComplete && !task.availableAfterSession) {
          pendDel += 1;
          deliverableList.push({ weekTitle: week.title, title: task.title, refId: task.refId });
        }
      }
    }
    return {
      overallProgress: totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0,
      pendingContent: pendCont,
      pendingDeliverables: pendDel,
      pendingContentList: contentList,
      pendingDeliverableList: deliverableList,
      projectMilestoneComplete: projMilestone,
      challengeMilestoneComplete: chMilestone,
      hasProjectMilestone: hasProjMilestone,
      hasChallengeMilestone: hasChMilestone,
    };
  }, [weeklyData]);

  // Self-paced (perpetual) route: overdue mandatory content vs suggested plan dates
  const isSelfPacedRoute = isPerpetualRoute;
  const selfPacedTotalWeeks = lastProgramWeek ?? 0;
  const selfPacedCurrentWeek = activeEnrollment?.plan_start_at
    ? Math.min(getCurrentWeek(activeEnrollment.plan_start_at), Math.max(selfPacedTotalWeeks, 1))
    : null;
  const selfPacedCompleted = isSelfPacedRoute && overallProgress >= 100 && weeklyData.length > 0;
  const goToRouteContent = () => navigate(cohortId ? `/content?cohort=${cohortId}` : '/content');
  const overdueSelfPacedContent = useMemo(() => {
    if (!isSelfPacedRoute || !activeEnrollment?.plan_start_at) return 0;
    const planStart = new Date(activeEnrollment.plan_start_at).getTime();
    const now = Date.now();
    let overdue = 0;
    for (const week of weeklyData) {
      const due = planStart + week.weekNumber * 7 * 24 * 60 * 60 * 1000;
      if (due >= now) continue;
      for (const task of week.tasks) {
        if (task.type !== 'content' || !task.isActive) continue;
        overdue += task.total - task.completed;
      }
    }
    return overdue;
  }, [isSelfPacedRoute, activeEnrollment?.plan_start_at, weeklyData]);

  // Days until demo day
  const projectDeadlineStr = activeEnrollment?.project_deadline ?? activeEnrollment?.demo_day_date;
  const projectDeadlineDate = projectDeadlineStr ? parseISO(projectDeadlineStr) : null;
  const daysUntilDeadline = projectDeadlineDate ? Math.max(0, differenceInDays(projectDeadlineDate, new Date())) : null;

  // Fuente de verdad unificada con "Progreso Semanal":
  // Si el hito existe en la semana (project / challenge_lab), usamos su estado.
  // De lo contrario, caemos al cálculo local para no romper cohortes sin milestone.
  const hasSubmittedProjectFallback = projects.some(
    p => (p.cohort_id === selectedCohortId || p.cohort_id == null) && p.submitted === true
  );
  // El hito no contempla proyectos de equipo donde el usuario es colaborador,
  // por eso se combina con el cálculo local en vez de reemplazarlo.
  const hasSubmittedProject = hasProjectMilestone
    ? (projectMilestoneComplete || hasSubmittedProjectFallback)
    : hasSubmittedProjectFallback;

  // Challenge Lab status (fallback cuando no hay milestone semanal): mismo criterio
  // que useWeeklyProgress → requiere solución creada por el propio usuario.
  const { data: challengeLabData } = useQuery({
    queryKey: ['challenge-lab-status', profile?.id],
    queryFn: async () => {
      const { data: challenges } = await supabase.from('challenges').select('id').eq('user_id', profile!.id);
      const challengeCount = challenges?.length || 0;
      let solutionCount = 0;
      if (challengeCount > 0) {
        const ids = challenges!.map(c => c.id);
        // challenge_solutions NO tiene columna user_id; el usuario ya queda acotado porque
        // ids proviene de challenges filtradas por user_id. Contar solo por challenge_id
        // (sin (supabase as any), así el tipado detecta columnas inexistentes).
        const { count } = await supabase
          .from('challenge_solutions')
          .select('id', { count: 'exact', head: true })
          .in('challenge_id', ids);
        solutionCount = count || 0;
      }
      return { challenges: challengeCount, solutions: solutionCount, completed: challengeCount > 0 && solutionCount > 0 };
    },
    enabled: !!profile?.id,
  });
  const challengeLabCompleted = hasChallengeMilestone
    ? challengeMilestoneComplete
    : (challengeLabData?.completed ?? false);

  const levelInfo = diagnostic?.maturity_level ? MATURITY_LEVELS[diagnostic.maturity_level] || MATURITY_LEVELS[1] : null;

  // Next workshop & mentoring from schedule
  const nextWorkshop = upcomingEvents.find(e => e.type === 'workshop' || e.type === 'conferencia');
  const nextMentoring = upcomingEvents.find(e => e.type === 'mentoria');

  const formatEventDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return format(parseISO(dateStr), "EEEE, d 'de' MMMM yyyy", { locale: es });
  };

  const formatTime = (hour: number | null, minute: number | null) => {
    if (hour === null) return '';
    return `${String(hour).padStart(2, '0')}:${String(minute ?? 0).padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      {/* Cohort Selector */}
      {enrollments.length > 1 && (
        <CertificationHeader enrollments={enrollments} selectedCohortId={selectedCohortId} onCohortChange={setSelectedCohortId} />
      )}
      {/* Welcome & Progress */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 glass-card">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4">
              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-2xl font-display font-bold text-foreground mb-1">
                  ¡Hola, {firstName}! 👋
                </h2>
                <p className="text-muted-foreground mb-2">
                  Estás cursando <span className="text-primary font-semibold">{programName}</span>
                </p>
                {isSelfPacedRoute ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                    <Zap className="w-4 h-4 text-primary" />
                    <span className="font-medium">
                      Ruta a tu ritmo
                      {selfPacedCurrentWeek && selfPacedTotalWeeks > 0
                        ? ` · Semana ${selfPacedCurrentWeek} de ${selfPacedTotalWeeks}`
                        : ' · Aún no has iniciado tu plan'}
                    </span>
                  </div>
                ) : activeEnrollment?.start_date && activeEnrollment?.end_date ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                    <BookOpen className="w-4 h-4 text-primary" />
                    <span className="font-medium">
                      {format(parseISO(activeEnrollment.start_date), 'dd MMM', { locale: es })} - {format(parseISO(activeEnrollment.end_date), 'dd MMM yyyy', { locale: es })}
                    </span>
                  </div>
                ) : null}
                {badges.length > 0 && (
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    {badges
                      .filter(b => !['first_week', 'consistent', 'first_project', 'first_certification'].includes(b.badge_key))
                      .map(b => (
                        <span key={b.id} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">{getBadgeMeta(b.badge_key).name}</span>
                      ))}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                  <Button onClick={goToRouteContent} className="gap-2 w-full sm:w-auto">
                    <BookOpen className="w-4 h-4" />
                    Continuar Contenido
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/calendar')} className="gap-2 w-full sm:w-auto">
                    <Calendar className="w-4 h-4" />
                    Ver Calendario
                  </Button>

                </div>
              </div>
              <ProgressRing progress={overallProgress} />
            </div>
          </CardContent>
        </Card>

        {/* Estado de proyecto / certificación (reemplaza "Días para entregar"): 5 estados.
            Prioridad: certificado emitido > aprobado > rechazado > entregado (en revisión) > no entregado. */}
        {activeEnrollment && cohortId && (
          isSelfPacedRoute ? (
            /* Ruta de autoaprendizaje: sin proyecto final, Demo Day ni certificado */
            <Card className={`glass-card ${selfPacedCompleted ? 'border-success/50' : 'border-primary/30'}`}>
              <CardContent className="p-6 flex flex-col items-center justify-center text-center h-full">
                {selfPacedCompleted ? (
                  <>
                    <CheckCircle2 className="w-10 h-10 mb-3 text-success" />
                    <p className="text-lg font-display font-bold text-foreground">Ruta completada</p>
                    <p className="text-sm text-muted-foreground mt-1 mb-4">Completaste todo el contenido de esta ruta a tu ritmo.</p>
                  </>
                ) : (
                  <>
                    <Zap className="w-10 h-10 mb-3 text-primary" />
                    <p className="text-lg font-display font-bold text-foreground">Tu ruta a tu ritmo</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {selfPacedCurrentWeek && selfPacedTotalWeeks > 0
                        ? `Semana ${selfPacedCurrentWeek} de ${selfPacedTotalWeeks} · ${overallProgress}% completado`
                        : 'Aún no has iniciado tu plan'}
                    </p>
                    {overdueSelfPacedContent > 0 && (
                      <p className="text-xs text-warning mt-2">{overdueSelfPacedContent} contenidos vencidos según tu plan</p>
                    )}
                  </>
                )}
                <div className="flex flex-col gap-2 w-full mt-4">
                  <Button size="sm" className="gap-2 w-full" onClick={goToRouteContent}>
                    <BookOpen className="w-4 h-4" />
                    {selfPacedCompleted ? 'Repasar contenido' : selfPacedCurrentWeek ? 'Continuar ruta' : 'Comenzar ruta'}
                  </Button>
                  <Button size="sm" variant="outline" className="gap-2 w-full" onClick={() => navigate('/calendar')}>
                    <Calendar className="w-4 h-4" />
                    Ver calendario
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : certificateAvailable ? (
            /* Estado 5: certificado emitido → se muestra el certificado de la cohorte aquí mismo */
            <CertificationsWidget cohortId={cohortId} />
          ) : projectApproved ? (
            /* Estado: proyecto aprobado, certificado aún no emitido */
            <Card className="glass-card border-success/50">
              <CardContent className="p-6 flex flex-col items-center justify-center text-center h-full">
                <CheckCircle2 className="w-10 h-10 mb-3 text-success" />
                <p className="text-lg font-display font-bold text-foreground">Proyecto aprobado</p>
                <p className="text-sm text-muted-foreground mt-1 mb-4">¡Felicitaciones! Tu certificado se generará pronto.</p>
                <span className="text-xs font-medium text-success bg-success/10 px-3 py-1 rounded-full">Aprobado</span>
              </CardContent>
            </Card>
          ) : projectRejected ? (
            /* Estado: proyecto rechazado → mostrar motivo + CTA para corregir */
            <Card className="glass-card border-destructive/50">
              <CardContent className="p-6 flex flex-col items-center justify-center text-center h-full">
                <AlertTriangle className="w-10 h-10 mb-3 text-destructive" />
                <p className="text-lg font-display font-bold text-foreground">Proyecto rechazado</p>
                {rejectionReason && (
                  <p className="text-sm text-muted-foreground mt-1 mb-2">{rejectionReason}</p>
                )}
                <p className="text-xs text-muted-foreground mb-4">Corregí lo indicado y volvé a enviar tu proyecto.</p>
                <Button
                  size="sm"
                  className="gap-2"
                  onClick={() => {
                    const mp = projects.find(p => p.cohort_id === selectedCohortId);
                    navigate(mp ? `/project/${mp.id}` : '/challenge');
                  }}
                >
                  <ArrowRight className="w-4 h-4" />
                  Corregir mi proyecto
                </Button>
              </CardContent>
            </Card>
          ) : hasSubmittedProject ? (
            /* Estado 2: proyecto entregado, aún sin certificado → en revisión */
            <Card className="glass-card border-primary/50">
              <CardContent className="p-6 flex flex-col items-center justify-center text-center h-full">
                <CheckCircle2 className="w-10 h-10 mb-3 text-primary" />
                <p className="text-lg font-display font-bold text-foreground">Proyecto entregado</p>
                <p className="text-sm text-muted-foreground mt-1 mb-4">En revisión para generar tu certificado.</p>
                <span className="text-xs font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">En revisión</span>
              </CardContent>
            </Card>
          ) : (
            /* Estado 1: proyecto NO entregado → aviso + CTA */
            <Card className="glass-card border-warning/50">
              <CardContent className="p-6 flex flex-col items-center justify-center text-center h-full">
                <AlertTriangle className="w-10 h-10 mb-3 text-warning" />
                <p className="text-lg font-display font-bold text-foreground">Aún no has entregado tu proyecto</p>
                <p className="text-sm text-muted-foreground mt-1 mb-4">
                  {daysUntilDeadline !== null
                    ? `Te quedan ${daysUntilDeadline} días para entregarlo.`
                    : 'Entrégalo para poder generar tu certificado.'}
                </p>
                <Button
                  size="sm"
                  className="gap-2"
                  onClick={() => {
                    const mp = projects.find(p => p.cohort_id === selectedCohortId);
                    navigate(mp ? `/project/${mp.id}` : '/challenge');
                  }}
                >
                  <ArrowRight className="w-4 h-4" />
                  Entregar mi proyecto
                </Button>
              </CardContent>
            </Card>
          )
        )}
      </div>

      {/* Diagnostic CTA or Result */}
      {loadingDiag ? (
        <Skeleton className="h-24 w-full" />
      ) : !diagnostic?.completed_at ? (
        <Card className="glass-card border-primary/30 bg-gradient-to-r from-primary/5 to-primary/10 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/20 shrink-0">
                <ClipboardCheck className="w-8 h-8 text-primary" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-display font-bold text-foreground mb-2">Completa tu Diagnóstico</h3>
                <p className="text-muted-foreground">Descubre tu nivel de madurez y recibe recomendaciones personalizadas.</p>
              </div>
              <Button onClick={() => navigate('/diagnostic')} className="shrink-0 gap-2" size="lg">
                Iniciar Diagnóstico <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Main Widgets Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          {/* Pendientes (componente compartido con /calendar) */}
          <PendingItemsWidget
            data={{
              pendingContent,
              pendingDeliverables,
              hasSubmittedProject,
              challengeLabCompleted,
              certificateAvailable,
              selfPaced: isSelfPacedRoute,
              overdueContent: overdueSelfPacedContent,
            }}
            onShowSection={(section) => setPendingModalSection(section)}
          />

          {/* Pending Details Modal — section-filtered */}
          <Dialog open={pendingModalSection !== null} onOpenChange={(open) => !open && setPendingModalSection(null)}>
            <DialogContent className="max-w-lg max-h-[85vh]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {pendingModalSection === 'content' ? (
                    <>
                      <Video className="w-5 h-5 text-primary" />
                      Contenido obligatorio pendiente ({pendingContentList.length})
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5 text-primary" />
                      Entregables pendientes ({pendingDeliverableList.length})
                    </>
                  )}
                </DialogTitle>
              </DialogHeader>
              <ScrollArea className="max-h-[65vh] pr-2">
                {pendingModalSection === 'content' && (
                  pendingContentList.length === 0 ? (
                    <p className="text-sm text-success">✓ Todo el contenido completado</p>
                  ) : (
                    <div className="space-y-2">
                      {pendingContentList.map((item, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            setPendingModalSection(null);
                            navigate(`/content?open=${item.contentId}&mandatory=1`);
                          }}
                          className="w-full flex items-start gap-3 p-2.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-left"
                        >
                          <span className="mt-1.5 w-2 h-2 rounded-full bg-warning shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                            <p className="text-xs text-muted-foreground">Semana: {item.weekTitle}</p>
                            <p className="text-xs text-muted-foreground">Tipo: Contenido</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )
                )}

                {pendingModalSection === 'deliverables' && (
                  pendingDeliverableList.length === 0 ? (
                    <p className="text-sm text-success">✓ Todos los entregables enviados</p>
                  ) : (
                    <div className="space-y-2">
                      {pendingDeliverableList.map((item, i) => (
                        <button
                          key={i}
                          type="button"
                          disabled={!item.refId}
                          onClick={() => {
                            setPendingModalSection(null);
                            if (item.refId) navigate(`/calendar?openDeliverable=${item.refId}`);
                            else navigate('/calendar');
                          }}
                          className="w-full flex items-start gap-3 p-2.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-left"
                        >
                          <span className="mt-1.5 w-2 h-2 rounded-full bg-warning shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
                            <p className="text-xs text-muted-foreground">Semana: {item.weekTitle}</p>
                            <p className="text-xs text-muted-foreground">Tipo: Entregable</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )
                )}
              </ScrollArea>
            </DialogContent>
          </Dialog>

          {/* Streak Ranking */}
          <StreakRankingWidget />

          {/* Next Workshop */}
          {nextWorkshop && (
            <Card className="glass-card">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Video className="w-5 h-5 text-accent" />
                  <CardTitle className="text-lg font-display">Próxima Sesión en Vivo</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span className="capitalize">{formatEventDate(nextWorkshop.scheduled_date)}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {formatTime(nextWorkshop.start_hour, nextWorkshop.start_minute)}
                      {nextWorkshop.end_hour !== null && ` - ${formatTime(nextWorkshop.end_hour, nextWorkshop.end_minute)}`}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-foreground">{nextWorkshop.title}</p>
                  {nextWorkshop.connection_link && (
                    <Button variant="outline" size="sm" className="w-full gap-2" onClick={() => window.open(nextWorkshop.connection_link!, '_blank')}>
                      <Video className="w-4 h-4" /> Conectarse
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Next Mentoring */}
          {nextMentoring && (
            <Card className="glass-card">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-success" />
                  <CardTitle className="text-lg font-display">Próxima Mentoría</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span className="capitalize">{formatEventDate(nextMentoring.scheduled_date)}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {formatTime(nextMentoring.start_hour, nextMentoring.start_minute)}
                      {nextMentoring.end_hour !== null && ` - ${formatTime(nextMentoring.end_hour, nextMentoring.end_minute)}`}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-foreground">{nextMentoring.title}</p>
                  {nextMentoring.connection_link && (() => {
                    // Ocultar boton si la sesion ya termino (hora actual >= hora fin del dia de la sesion)
                    const now = new Date();
                    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                    const isToday = nextMentoring.scheduled_date === today;
                    const currentMinutes = now.getHours() * 60 + now.getMinutes();
                    const endMinutes = (nextMentoring.end_hour ?? 23) * 60 + (nextMentoring.end_minute ?? 59);
                    const isOver = isToday && currentMinutes >= endMinutes;
                    if (isOver) return null;
                    return (
                      <Button size="sm" className="w-full gap-2" onClick={() => window.open(nextMentoring.connection_link!, '_blank')}>
                        <Video className="w-4 h-4" /> Unirse a la Sesión
                      </Button>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Help */}
          <Card className="glass-card border-primary/20">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-primary" />
                <CardTitle className="text-lg font-display">¿Necesitas ayuda?</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              <p className="text-sm text-muted-foreground mb-3">
                {isSelfPacedRoute
                  ? 'Si tienes dudas sobre el contenido de tu ruta, pregúntale a LIA cuando quieras.'
                  : 'Si tienes dudas sobre el contenido o tu proyecto, agenda una sesión con tu mentor.'}
              </p>
              <Button variant="outline" size="sm" className="w-full gap-2" onClick={() => setLiaChatOpen(true)}>
                <MessageCircle className="w-4 h-4" /> Chat con LIA
              </Button>
            </CardContent>
          </Card>
        </div>




        {/* Right Column: Weekly Progress */}
        <div className="space-y-4">
          {isLastCohortWeek && (
            <NPSInlineWidget
              triggerKey={`cohort_last_week:${activeEnrollment!.cohort_id}`}
              cohortId={activeEnrollment!.cohort_id}
            />
          )}
          <Card className="glass-card h-fit">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-primary" />
                <CardTitle className="text-lg font-display">Progreso Semanal</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              <WeeklyProgressAccordion
                programId={activeEnrollment?.program_id}
                cohortStartDate={routeStartDate}
                selfPaced={isSelfPacedRoute}
                cohortId={activeEnrollment?.cohort_id}
                deadlines={{
                  project_deadline: activeEnrollment?.project_deadline,
                  project_deadline_week: activeEnrollment?.project_deadline_week,
                  challenge_lab_deadline: activeEnrollment?.challenge_lab_deadline,
                  challenge_lab_deadline_week: activeEnrollment?.challenge_lab_deadline_week,
                }}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      <LiaChatDrawer isOpen={liaChatOpen} onClose={() => setLiaChatOpen(false)} />
    </div>
  );
}
